import { useContext, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { toast } from '@raystack/apsara';
import type { Project } from '@raystack/frontier';
import { ListProjectsByCurrentUserRequestSchema } from '@raystack/frontier';
import {
  FrontierServiceQueries,
  create,
  useQuery
} from '@raystack/frontier/hooks';

import { IdentityContext } from '@src/contexts/identity/context';
import {
  setActiveAoi,
  setProjectList
} from '@src/redux/_reducers/projectReducer';

type ConnectError = {
  code?: number;
  message?: string;
};

interface UseProjectsOptions {
  orgId?: string;
  canUserUpdate?: boolean;
}

export function useProjects(options: UseProjectsOptions = {}) {
  const { activeOrg } = useContext(IdentityContext);
  const { orgId = activeOrg?.id, canUserUpdate = false } = options;

  const dispatch = useDispatch();

  // Memoize query input to prevent infinite re-renders
  const queryInput = useMemo(
    () =>
      create(
        ListProjectsByCurrentUserRequestSchema,
        canUserUpdate
          ? { orgId, nonInherited: true, withPermissions: ['update'] }
          : { orgId, nonInherited: true }
      ),
    [orgId, canUserUpdate]
  );

  // Reactive query - auto-fetches when orgId changes
  const { data, isLoading, error, refetch } = useQuery(
    FrontierServiceQueries.listProjectsByCurrentUser,
    queryInput,
    {
      enabled: !!orgId
    }
  );

  // Memoize to prevent new array references on every render
  const allProjects = useMemo(() => data?.projects ?? [], [data?.projects]);
  const accessPairs = useMemo(() => data?.accessPairs ?? [], [data?.accessPairs]);

  // If canUserUpdate, filter projects based on accessPairs
  const projects = useMemo(() => {
    if (!canUserUpdate) {
      return allProjects;
    }

    // Set of project IDs with 'update' permission
    const writableProjectIds = new Set(
      accessPairs
        ?.filter(({ permissions }) => permissions?.includes('update'))
        ?.map(({ projectId }) => projectId) ?? []
    );

    // Filter + sort projects that have write access
    return allProjects
      .filter(({ id }) => writableProjectIds.has(id))
      .sort(
        (a, b) =>
          a.title?.localeCompare(b.title ?? '', undefined, {
            sensitivity: 'base'
          }) ?? 0
      );
  }, [allProjects, accessPairs, canUserUpdate]);

  // Create projectsMap for easy lookups
  const projectsMap = useMemo(() => {
    return allProjects.reduce<Record<string, Project>>((acc, curr) => {
      if (curr.id) {
        acc[curr.id] = curr;
      }
      return acc;
    }, {});
  }, [allProjects]);

  // Sync with Redux (for backward compatibility)
  useEffect(() => {
    dispatch(setProjectList(allProjects));
  }, [allProjects, dispatch]);

  // Error handling
  useEffect(() => {
    if (!error) return;

    const connectError = error as ConnectError;

    // Code 3 = INVALID_ARGUMENT (similar to 400 Bad Request)
    if (connectError.code === 3) {
      console.error(connectError);
      toast.error('Something went wrong!', {
        description: connectError.message
      });
      return;
    }

    // Code 5 = NOT_FOUND (similar to 404 Not Found)
    if (connectError.code === 5) {
      dispatch(setActiveAoi({}));
      return;
    }

    // Generic error
    toast.error('Oops! Something went wrong!', {
      description:
        'We experienced an unfortunate issue with our backend services while fetching projects, please try again after sometime!'
    });
    console.error(error);
  }, [error, dispatch]);

  return {
    projects,
    allProjects,
    projectsMap,
    isLoading,
    getProjects: refetch
  };
}
